import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusCircle, RotateCcw, X, History, ChevronRight, Trash2, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useCreateCourseMutation,
  useGetInstructorCoursesQuery,
  useDeleteCourseMutation,
} from '../../api/apiSlice';
import { readDraft, writeDraft, clearDraft } from '../../lib/draftStore';
import Loading from '../../components/common/Loading';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const DRAFT_KEY = 'createCourse';

const CreateCoursePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const draftValueRef = useRef(null);

  const [createCourse, { isLoading, error }] = useCreateCourseMutation();
  const { data: allCourses = [] } = useGetInstructorCoursesQuery();
  const [deleteCourse, { isLoading: isDeleting }] = useDeleteCourseMutation();

  const drafts = allCourses
    .filter((c) => c.status === 'draft' || c.status === 'rejected' || c.status === 'inactive')
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

  const locale = i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU';

  const draftToDelete = drafts.find((c) => c._id === pendingDeleteId);

  const handleDeleteDraft = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteCourse(pendingDeleteId).unwrap();
      toast.success(t('editor.draftDiscard'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.deleteError'));
    } finally {
      setPendingDeleteId(null);
    }
  };

  // Check for existing draft on mount
  useEffect(() => {
    const draft = readDraft(DRAFT_KEY);
    if (draft && typeof draft.title === 'string' && draft.title.trim()) {
      draftValueRef.current = draft.title;
      setShowRestorePrompt(true);
    }
  }, []);

  // Save draft on every keystroke (cheap, single field)
  useEffect(() => {
    if (!hasRestoredDraft && !title) return;
    if (title.trim()) {
      writeDraft(DRAFT_KEY, { title });
    } else {
      clearDraft(DRAFT_KEY);
    }
  }, [title, hasRestoredDraft]);

  const handleRestore = () => {
    setTitle(draftValueRef.current || '');
    setHasRestoredDraft(true);
    setShowRestorePrompt(false);
  };

  const handleDiscard = () => {
    clearDraft(DRAFT_KEY);
    setShowRestorePrompt(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const newCourse = await createCourse({ title: title.trim() }).unwrap();
      clearDraft(DRAFT_KEY);
      navigate(`/dashboard/instructor/create-course/${newCourse._id}/edit`);
    } catch (err) {
      console.error('Create course error:', err);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('editor.createTitle')}</h1>
      <p className="mt-2 text-slate-600 dark:text-slate-400">{t('editor.createSubtitle')}</p>

      {showRestorePrompt && (
        <div className="mt-6 max-w-lg rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-950/30 p-4 flex items-start gap-3">
          <RotateCcw className="text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" size={18} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 dark:text-white text-sm">{t('editor.draftRestoredTitle')}</p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{t('editor.draftRestoredDesc')}</p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={handleRestore}
                className="rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-indigo-700"
              >
                {t('editor.draftRestore')}
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {t('editor.draftDiscard')}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowRestorePrompt(false)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            aria-label={t('common.cancel')}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {drafts.length > 0 && (
        <section className="mt-8 max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <History size={18} className="text-slate-500 dark:text-slate-400" />
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              {t('editor.unfinishedTitle')}
            </h2>
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">({drafts.length})</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t('editor.unfinishedDesc')}</p>
          <ul className="space-y-2">
            {drafts.map((c) => (
              <li
                key={c._id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 flex items-center gap-3"
              >
                {c.coverImageUrl ? (
                  <img src={c.coverImageUrl} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                    <FileText size={18} className="text-slate-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-slate-900 dark:text-white">
                      {c.title || t('editor.newSection')}
                    </p>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        c.status === 'rejected' || c.status === 'inactive'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                      }`}
                    >
                      {t(c.status === 'rejected' || c.status === 'inactive' ? 'course.status.rejected' : 'course.status.draft')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {c.updatedAt ? new Date(c.updatedAt).toLocaleString(locale) : '—'}
                    {c.rejectionReason && (
                      <span className="ml-2 italic text-red-600 dark:text-red-400">
                        — {c.rejectionReason}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingDeleteId(c._id)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400"
                  title={t('common.delete')}
                >
                  <Trash2 size={16} />
                </button>
                <Link
                  to={`/dashboard/instructor/create-course/${c._id}/edit`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700"
                >
                  {t('editor.continue')}
                  <ChevronRight size={14} />
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('editor.orCreateNew')}</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
          </div>
        </section>
      )}

      <form onSubmit={handleCreate} className="mt-6 max-w-lg">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 sm:p-8 rounded-2xl shadow-sm">
          {error && (
            <p className="text-red-500 mb-4 text-sm">
              {error.data?.message || t('errors.generic')}
            </p>
          )}
          <label htmlFor="title" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
            {t('editor.courseTitle')}
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            placeholder={t('editor.courseTitlePlaceholder')}
            required
            autoFocus
          />

          <div className="mt-6 border-t dark:border-slate-700 pt-6">
            <button
              type="submit"
              disabled={isLoading || !title.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loading variant="button" size="sm" />
                  {t('editor.creating')}
                </>
              ) : (
                <>
                  <PlusCircle size={18} />
                  {t('editor.createButton')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title={t('editor.deleteDraftConfirmTitle')}
        description={t('editor.deleteDraftConfirmDesc', { title: draftToDelete?.title || '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={isDeleting}
        onConfirm={handleDeleteDraft}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default CreateCoursePage;
